import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle,
  Archive,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react'
import { APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY } from '../../lib/appStateBackupProgress'
import type {
  AppStateBackupContentGroupSummary,
  AppStateBackupOperation,
  AppStateBackupOperationToken,
  AppStateBackupProgressEvent,
  AppStateBackupWorkspaceInventory,
  AppStateBackupWorkSummary,
} from '../../lib/appStateBackupProgress'
import {
  beginAppStateBackupProgress,
  clearAppStateBackupProgressDisplay,
  completeAppStateBackupProgress,
  failAppStateBackupProgress,
  getActiveAppStateBackupOperation,
  isActiveAppStateBackupOperation,
  reportAppStateBackupProgress,
  subscribeToAppStateBackupProgress,
} from '../../lib/appStateBackupProgressCoordinator'
import './AppStateBackupProgressOverlay.css'

export { APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY } from '../../lib/appStateBackupProgress'

type OverlayStatus = 'active' | 'complete' | 'error'

interface OverlayState {
  status: OverlayStatus
  event: AppStateBackupProgressEvent
}

interface CompleteOptions {
  persistAcrossReload?: boolean
}

interface BackupProgressContextValue {
  isBusy: boolean
  activeOperation: AppStateBackupOperation | null
  begin: (operation: AppStateBackupOperation) => AppStateBackupOperationToken | null
  report: (token: AppStateBackupOperationToken, event: AppStateBackupProgressEvent) => void
  complete: (
    token: AppStateBackupOperationToken,
    operation: AppStateBackupOperation,
    message: string,
    options?: CompleteOptions
  ) => boolean
  fail: (token: AppStateBackupOperationToken, operation: AppStateBackupOperation, message: string) => void
}

interface CompletionHandoff {
  version: 2
  savedAt: number
  operation: AppStateBackupOperation
  message: string
}

const COMPLETION_VISIBLE_MS = 7000
const COMPLETION_HANDOFF_MAX_AGE_MS = 2 * 60 * 1000

const BackupProgressContext = createContext<BackupProgressContextValue>({
  isBusy: false,
  activeOperation: null,
  begin: () => null,
  report: () => undefined,
  complete: () => false,
  fail: () => undefined,
})

const initialEvent = (operation: AppStateBackupOperation): AppStateBackupProgressEvent => ({
  operation,
  phase: operation === 'export' ? 'waiting-for-edits' : 'reading-file',
  percent: 1,
  message: operation === 'export' ? 'Starting your backup' : 'Reading the selected backup',
  detail: operation === 'export'
    ? 'Recent saved edits will finish before collection begins.'
    : 'Your current workspace will not change while the file is being read.',
})

const removeCompletionHandoff = (): void => {
  try {
    window.sessionStorage.removeItem(APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY)
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
  try {
    const currentState = window.history.state
    if (currentState
      && typeof currentState === 'object'
      && Object.prototype.hasOwnProperty.call(currentState, APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY)) {
      const nextState = { ...currentState }
      delete nextState[APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY]
      window.history.replaceState(nextState, '')
    }
  } catch {
    // History state can also be unavailable in restricted browser contexts.
  }
}

const saveCompletionHandoff = (
  operation: AppStateBackupOperation,
  message: string
): boolean => {
  const handoff: CompletionHandoff = { version: 2, savedAt: Date.now(), operation, message }
  let persisted = false
  try {
    window.sessionStorage.setItem(APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY, JSON.stringify(handoff))
    persisted = true
  } catch {
    // Try history state below so a same-entry reload can still show completion.
  }
  try {
    const currentState = window.history.state
    const nextState = currentState && typeof currentState === 'object'
      ? { ...currentState, [APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY]: handoff }
      : { [APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY]: handoff }
    window.history.replaceState(nextState, '')
    persisted = true
  } catch {
    // The caller skips automatic reload when neither persistence route is available.
  }
  return persisted
}

const readCompletionHandoff = (): OverlayState | null => {
  const candidates: unknown[] = []
  try {
    const raw = window.sessionStorage.getItem(APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY)
    if (raw) candidates.push(JSON.parse(raw))
  } catch {
    // A valid history-state fallback may still be available.
  }
  try {
    const currentState = window.history.state
    if (currentState && typeof currentState === 'object') {
      candidates.push(currentState[APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY])
    }
  } catch {
    // Treat an unavailable history state as no handoff.
  }

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue
    const handoff = candidate as Partial<CompletionHandoff>
    const handoffAge = typeof handoff.savedAt === 'number' ? Date.now() - handoff.savedAt : Number.NaN
    if (handoff.version !== 2
      || typeof handoff.savedAt !== 'number'
      || !Number.isFinite(handoffAge)
      || handoffAge < 0
      || handoffAge > COMPLETION_HANDOFF_MAX_AGE_MS
      || (handoff.operation !== 'export' && handoff.operation !== 'import')
      || typeof handoff.message !== 'string'
      || handoff.message.trim().length === 0
      || handoff.message.length > 500) continue

    const state: OverlayState = {
      status: 'complete',
      event: {
        operation: handoff.operation,
        phase: 'ready',
        percent: 100,
        message: handoff.message,
        detail: handoff.operation === 'export'
          ? 'The backup was prepared.'
          : 'The restored workspace is ready.',
      },
    }
    removeCompletionHandoff()
    return state
  }

  removeCompletionHandoff()
  return null
}

export function AppStateBackupProgressProvider({ children }: { children: React.ReactNode }) {
  const [overlay, setOverlay] = useState<OverlayState | null>(null)
  const overlayRef = useRef<OverlayState | null>(null)
  const [activeOperation, setActiveOperation] = useState<AppStateBackupOperation | null>(
    getActiveAppStateBackupOperation()
  )
  const dismissTimerRef = useRef<number | null>(null)

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = null
    }
  }, [])

  const dismiss = useCallback(() => {
    clearDismissTimer()
    removeCompletionHandoff()
    clearAppStateBackupProgressDisplay()
    overlayRef.current = null
    setOverlay(null)
  }, [clearDismissTimer])

  const scheduleCompletionDismissal = useCallback(() => {
    clearDismissTimer()
    dismissTimerRef.current = window.setTimeout(dismiss, COMPLETION_VISIBLE_MS)
  }, [clearDismissTimer, dismiss])

  useEffect(() => {
    return subscribeToAppStateBackupProgress(signal => {
      if (signal.kind === 'begin') {
        clearDismissTimer()
        removeCompletionHandoff()
        setActiveOperation(signal.operation)
        const next: OverlayState = { status: 'active', event: initialEvent(signal.operation) }
        overlayRef.current = next
        setOverlay(next)
        return
      }
      if (signal.kind === 'report') {
        clearDismissTimer()
        const next: OverlayState = {
          status: 'active',
          event: {
            ...signal.event,
            inventory: signal.event.inventory || overlayRef.current?.event.inventory,
          },
        }
        overlayRef.current = next
        setOverlay(next)
        return
      }
      if (signal.kind === 'complete') {
        clearDismissTimer()
        setActiveOperation(null)
        const next: OverlayState = {
          status: 'complete',
          event: {
            operation: signal.operation,
            phase: 'ready',
            percent: 100,
            message: signal.message,
            detail: signal.operation === 'export'
              ? 'The backup has been prepared. The contents of the file are listed below.'
              : signal.persistAcrossReload
                ? 'The restored workspace is ready and will now reopen.'
                : 'The restored workspace is ready.',
            inventory: overlayRef.current?.event.inventory,
          },
        }
        overlayRef.current = next
        setOverlay(next)
        scheduleCompletionDismissal()
        return
      }

      clearDismissTimer()
      setActiveOperation(null)
      removeCompletionHandoff()
      const next: OverlayState = {
        status: 'error',
        event: {
          operation: signal.operation,
          phase: 'ready',
          percent: null,
          message: signal.operation === 'export' ? 'The backup could not be completed' : 'The backup could not be restored',
          detail: signal.message,
          inventory: overlayRef.current?.event.inventory,
        },
      }
      overlayRef.current = next
      setOverlay(next)
    })
  }, [clearDismissTimer, scheduleCompletionDismissal])

  useEffect(() => {
    if (getActiveAppStateBackupOperation()) {
      removeCompletionHandoff()
      return clearDismissTimer
    }
    const handoff = readCompletionHandoff()
    if (handoff) {
      overlayRef.current = handoff
      setOverlay(handoff)
      scheduleCompletionDismissal()
    }
    return clearDismissTimer
  }, [clearDismissTimer, scheduleCompletionDismissal])

  const begin = useCallback((operation: AppStateBackupOperation): AppStateBackupOperationToken | null => (
    beginAppStateBackupProgress(operation)
  ), [])

  const report = useCallback((token: AppStateBackupOperationToken, event: AppStateBackupProgressEvent) => {
    reportAppStateBackupProgress(token, event)
  }, [])

  const complete = useCallback((
    token: AppStateBackupOperationToken,
    operation: AppStateBackupOperation,
    message: string,
    options: CompleteOptions = {}
  ): boolean => {
    if (!isActiveAppStateBackupOperation(token, operation)) return false
    const persistenceRequested = options.persistAcrossReload === true
    const handoffPersisted = !persistenceRequested || saveCompletionHandoff(operation, message)
    completeAppStateBackupProgress(
      token,
      operation,
      message,
      persistenceRequested && handoffPersisted
    )
    return handoffPersisted
  }, [])

  const fail = useCallback((
    token: AppStateBackupOperationToken,
    operation: AppStateBackupOperation,
    message: string
  ) => {
    failAppStateBackupProgress(token, operation, message)
  }, [])

  const value = useMemo(() => ({
    isBusy: activeOperation !== null,
    activeOperation,
    begin,
    report,
    complete,
    fail,
  }), [activeOperation, begin, complete, fail, report])

  const overlayElement = overlay
    ? <AppStateBackupProgressOverlay state={overlay} onDismiss={dismiss} />
    : null

  return (
    <BackupProgressContext.Provider value={value}>
      {children}
      {overlayElement && typeof document !== 'undefined'
        ? createPortal(overlayElement, document.body)
        : overlayElement}
    </BackupProgressContext.Provider>
  )
}

export const useAppStateBackupProgress = (): BackupProgressContextValue => useContext(BackupProgressContext)

const plural = (count: number, singular: string, pluralLabel = `${singular}s`): string => (
  `${count.toLocaleString()} ${count === 1 ? singular : pluralLabel}`
)

function GroupRows({ groups }: { groups: AppStateBackupContentGroupSummary[] }) {
  return (
    <div className="app-backup-cascade-line ml-[7px] mt-2 border-l border-black/25 pl-4">
      {groups.map((group, index) => (
        <div
          key={group.key}
          className="app-backup-cascade-item relative pb-2 last:pb-0"
          style={{ '--cascade-index': index + 1 } as React.CSSProperties}
        >
          <span className="absolute -left-[19px] top-[5px] h-1.5 w-1.5 border border-black bg-[#fffdfa]" aria-hidden="true" />
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.11em] text-gray-700">{group.label}</div>
            <div className="shrink-0 font-mono text-[10px] font-bold text-gray-700">{group.itemCount.toLocaleString()}</div>
          </div>
          <div className="mt-0.5 text-[10px] leading-4 text-gray-600">{group.detail}</div>
        </div>
      ))}
    </div>
  )
}

function WorkCascade({ work }: { work: AppStateBackupWorkSummary }) {
  return (
    <div className="border border-black/20 bg-white px-3 py-2.5" data-testid={`backup-progress-work-${work.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <BookOpen className="h-3.5 w-3.5 shrink-0 text-gray-600" aria-hidden="true" />
          <span className="truncate text-xs font-semibold text-black">{work.title}</span>
        </div>
        <span className="shrink-0 text-[10px] text-gray-600">{plural(work.itemCount, 'saved item')}</span>
      </div>
      <GroupRows groups={work.groups} />
    </div>
  )
}

const legacyCollationDetail = (
  inventory: AppStateBackupWorkspaceInventory,
  operation: AppStateBackupOperation
): string => {
  if (inventory.legacyCollation.status === 'not-present-preserved') {
    return operation === 'import'
      ? 'Not present in this file; existing older collations stay unchanged.'
      : 'Not included in this backup format.'
  }
  if (inventory.legacyCollation.itemCount === 0) {
    return operation === 'export' ? 'Included; none are currently saved.' : 'Included; this backup contains none.'
  }
  return `${plural(inventory.legacyCollation.itemCount, 'saved item')} ${operation === 'export' ? 'included' : 'restored'}.`
}

function WorkspaceInventory({
  inventory,
  operation,
}: {
  inventory: AppStateBackupWorkspaceInventory
  operation: AppStateBackupOperation
}) {
  return (
    <div className="max-h-[min(58vh,520px)] space-y-2 overflow-y-auto border-t border-black/20 bg-[#f3efe6] p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-700">
          {operation === 'export' ? 'Included in this backup' : 'Contents of this backup'}
        </span>
        <span className="text-[10px] text-gray-600">{plural(inventory.primaryItemCount, 'browser-saved item')}</span>
      </div>

      {inventory.works.map(work => <WorkCascade key={work.id} work={work} />)}

      {inventory.sharedGroups.length > 0 && (
        <div className="border border-black/20 bg-white px-3 py-2.5" data-testid="backup-progress-shared-data">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Archive className="h-3.5 w-3.5 shrink-0 text-gray-600" aria-hidden="true" />
              <span className="text-xs font-semibold text-black">Shared or unassigned workspace data</span>
            </div>
            <span className="shrink-0 text-[10px] text-gray-600">{plural(inventory.sharedItemCount, 'saved item')}</span>
          </div>
          <GroupRows groups={inventory.sharedGroups} />
        </div>
      )}

      <div className="border border-black/20 bg-white px-3 py-2.5">
        <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-gray-700">Workspace recovery</div>
        <dl className="mt-2 space-y-2 text-[10px] leading-4 text-gray-600">
          <div>
            <dt className="font-semibold text-gray-800">Browser settings and saved route</dt>
            <dd>{plural(inventory.browserStateItemCount, 'saved item')}.</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">Resume information</dt>
            <dd>{plural(inventory.resumeItemCount, 'saved item')}.</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">Older saved collations</dt>
            <dd>{legacyCollationDetail(inventory, operation)}</dd>
          </div>
        </dl>
      </div>

      <div className="border border-amber-900/40 bg-amber-50 px-3 py-2.5">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.13em] text-amber-950">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
          Not in this file
        </div>
        <dl className="mt-2 space-y-2 text-[10px] leading-4 text-amber-950/80">
          {inventory.excluded.map(item => (
            <div key={item.id}>
              <dt className="font-semibold text-amber-950">{item.label}</dt>
              <dd>{item.detail}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

function AppStateBackupProgressOverlay({ state, onDismiss }: { state: OverlayState; onDismiss: () => void }) {
  const [collapsed, setCollapsed] = useState(false)
  const { event, status } = state
  const isRecovering = event.phase === 'recovering-current-work'
  const active = status === 'active'
  const detailsId = 'app-state-backup-progress-details'
  const title = status === 'complete'
    ? event.operation === 'export' ? 'Backup ready' : 'Restore complete'
    : status === 'error'
      ? event.operation === 'export' ? 'Backup stopped' : 'Restore stopped'
      : isRecovering
        ? 'Protecting your current work'
        : event.operation === 'export' ? 'Creating your backup' : 'Restoring your backup'

  return (
    <aside
      className="app-backup-system-overlay app-backup-overlay-enter fixed right-3 top-3 w-[min(380px,calc(100vw-24px))] border border-black bg-[#fffdfa] text-black shadow-[4px_4px_0_#000] sm:right-4 sm:top-4"
      aria-label={title}
      data-testid="app-state-backup-progress-overlay"
    >
      <div
        className="sr-only"
        role={status === 'error' ? 'alert' : 'status'}
        aria-live={status === 'error' ? 'assertive' : 'polite'}
        aria-atomic="true"
      >
        {title}. {event.message}{status === 'error' && event.detail ? `. ${event.detail}` : ''}
      </div>

      <div className={`flex items-center gap-3 border-b border-black px-3 py-2.5 ${status === 'error' ? 'bg-red-950' : isRecovering ? 'bg-amber-950' : 'bg-black'} text-white`}>
        {status === 'complete' ? (
          <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : status === 'error' ? (
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <Archive className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/65">Variorum workspace</div>
          <div className="truncate text-sm font-semibold">{title}</div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(value => !value)}
          className="grid h-7 w-7 place-items-center border border-white/35 text-white transition-colors hover:bg-white/10"
          aria-label={collapsed ? 'Show backup details' : 'Hide backup details'}
          aria-expanded={!collapsed}
          aria-controls={detailsId}
        >
          {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>
        {!active && (
          <button
            type="button"
            onClick={onDismiss}
            className="grid h-7 w-7 place-items-center border border-white/35 text-white transition-colors hover:bg-white/10"
            aria-label="Dismiss backup progress"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="px-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold leading-5 text-black">{event.message}</div>
            {!collapsed && event.detail && <div className="mt-1 text-[11px] leading-4 text-gray-600">{event.detail}</div>}
          </div>
          {event.percent !== null && <div className="shrink-0 font-mono text-xs font-bold tabular-nums">{event.percent}%</div>}
        </div>

        <div
          className="mt-3 h-2 overflow-hidden border border-black bg-gray-200"
          role="progressbar"
          aria-label={event.message}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={event.percent ?? undefined}
        >
          <div
            className={`${event.percent === null ? 'app-backup-progress-indeterminate' : 'app-backup-progress-fill'} h-full ${status === 'error' ? 'bg-red-800' : isRecovering ? 'bg-amber-700' : 'bg-black'}`}
            style={event.percent === null ? undefined : { width: `${event.percent}%` }}
          />
        </div>
      </div>

      <div id={detailsId}>
        {!collapsed && event.inventory && (
          <WorkspaceInventory inventory={event.inventory} operation={event.operation} />
        )}
        {!collapsed && !event.inventory && (
          <div className="flex items-center gap-2 border-t border-black/20 bg-white px-3 py-2 text-[10px] leading-4 text-gray-600">
            <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
              {status === 'active'
                ? 'The app will list every included category after the workspace has been checked.'
                : status === 'complete'
                  ? 'The detailed contents were checked before this operation completed.'
                  : 'No detailed item list is available for this stopped operation.'}
            </span>
          </div>
        )}
      </div>
    </aside>
  )
}
